import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Icon, IconName } from '../../atoms/Icon';

export interface ActivityLogEntry {
  id: string;
  timestamp: string;
  type: 'system' | 'user';
  actor?: string;
  action: string;
  details?: string;
  icon?: IconName;
}

export interface ActivityLogProps {
  entries: ActivityLogEntry[];
  title?: string;
  maxHeight?: string;
  isLoading?: boolean;
  emptyMessage?: string;
}

const ActivityLogContainer = styled(motion.div)<{ $maxHeight?: string }>`
  width: 100%;
  max-height: ${({ $maxHeight }) => $maxHeight || 'none'};
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.gray100};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.gray300};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    
    &:hover {
      background: ${({ theme }) => theme.colors.gray400};
    }
  }
`;

const ActivityLogTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[5]} 0;
`;

const ActivityLogList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const ActivityLogItem = styled(motion.div)<{ $type: 'system' | 'user' }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]};
  border-left: 3px solid ${({ $type, theme }) => 
    $type === 'system' ? theme.colors.info : theme.colors.primary
  };
  background: ${({ theme }) => theme.colors.gray100};
  border-radius: 0 ${({ theme }) => theme.borderRadius.sm} ${({ theme }) => theme.borderRadius.sm} 0;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.gray200};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

const ActivityIcon = styled.div<{ $type: 'system' | 'user' }>`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $type, theme }) => 
    $type === 'system' ? `${theme.colors.info}20` : `${theme.colors.primary}20`
  };
  color: ${({ $type, theme }) => 
    $type === 'system' ? theme.colors.info : theme.colors.primary
  };
  flex-shrink: 0;
  padding: ${({ theme }) => theme.spacing[2]};
`;

const ActivityContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  flex: 1;
  min-width: 0;
`;

const ActivityTime = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const ActivityAction = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ActivityActor = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
`;

const ActivityDetails = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray700};
  margin-top: ${({ theme }) => theme.spacing[1]};
  line-height: 1.4;
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const LoadingState = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

export const ActivityLog: React.FC<ActivityLogProps> = ({
  entries,
  title,
  maxHeight,
  isLoading = false,
  emptyMessage = 'No activity logged yet',
}) => {
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDefaultIcon = (type: 'system' | 'user'): IconName => {
    return type === 'system' ? 'Zap' : 'User';
  };

  if (isLoading) {
    return (
      <ActivityLogContainer>
        {title && <ActivityLogTitle>{title}</ActivityLogTitle>}
        <LoadingState>Loading activity...</LoadingState>
      </ActivityLogContainer>
    );
  }

  if (entries.length === 0) {
    return (
      <ActivityLogContainer>
        {title && <ActivityLogTitle>{title}</ActivityLogTitle>}
        <EmptyState>{emptyMessage}</EmptyState>
      </ActivityLogContainer>
    );
  }

  // Sort entries by timestamp descending (most recent first)
  const sortedEntries = [...entries].sort((a, b) => 
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  return (
    <ActivityLogContainer $maxHeight={maxHeight}>
      {title && <ActivityLogTitle>{title}</ActivityLogTitle>}
      <ActivityLogList>
        {sortedEntries.map((entry, index) => (
          <ActivityLogItem
            key={entry.id}
            $type={entry.type}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <ActivityIcon $type={entry.type}>
              <Icon name={entry.icon || getDefaultIcon(entry.type)} size={16} />
            </ActivityIcon>
            
            <ActivityContent>
              <ActivityTime>{formatTimestamp(entry.timestamp)}</ActivityTime>
              <ActivityAction>{entry.action}</ActivityAction>
              {entry.actor && <ActivityActor>by {entry.actor}</ActivityActor>}
              {entry.details && <ActivityDetails>{entry.details}</ActivityDetails>}
            </ActivityContent>
          </ActivityLogItem>
        ))}
      </ActivityLogList>
    </ActivityLogContainer>
  );
};
